import { FilterList as FilterIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Menu,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import React, { useState } from 'react';
import { colors } from '../theme';

interface CustomDataGridProps<T extends GridValidRowModel> {
  rows: T[];
  columns: GridColDef[];
  loading?: boolean;
  onRowClick?: (params: GridRowParams<T>) => void;
  onColumnFilter?: (field: string, value: string) => void;
  noRowsLabel?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  rowHeight?: number;
  height?: string;
}

const CustomDataGrid = <T extends GridValidRowModel>({
  rows,
  columns,
  loading = false,
  onRowClick,
  onColumnFilter,
  noRowsLabel = 'Henüz veri bulunmuyor',
  pageSize = 15,
  pageSizeOptions = [10, 15, 25, 50, 100],
  rowHeight = 80,
  height = '100%',
}: CustomDataGridProps<T>) => {
  const [filterAnchor, setFilterAnchor] = useState<{
    element: HTMLElement;
    field: string;
  } | null>(null);
  const [filterValue, setFilterValue] = useState<string>('');

  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    field: string
  ) => {
    setFilterAnchor({ element: event.currentTarget, field });
    setFilterValue('');
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
    setFilterValue('');
  };

  const handleFilterApply = () => {
    if (filterAnchor && onColumnFilter) {
      onColumnFilter(filterAnchor.field, filterValue);
    }
    handleFilterClose();
  };

  const handleFilterClear = () => {
    if (filterAnchor && onColumnFilter) {
      onColumnFilter(filterAnchor.field, '');
    }
    handleFilterClose();
  };

  // Column'ları filter icon ile güncelle
  const columnsWithFilter = columns.map((column) => {
    const originalRenderHeader = column.renderHeader;

    return {
      ...column,
      renderHeader: (params: any) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {originalRenderHeader ? (
              originalRenderHeader(params)
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: colors.text.primary,
                }}
              >
                {params.colDef.headerName || params.field}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onColumnFilter) {
                handleFilterClick(e, params.field);
              }
            }}
            disabled={!onColumnFilter}
            sx={{
              padding: '4px',
              minWidth: '28px',
              width: '28px',
              height: '28px',
              color: onColumnFilter
                ? colors.primary.main
                : colors.text.disabled,
              bgcolor: onColumnFilter ? colors.background.light : 'transparent',
              '&:hover': onColumnFilter
                ? {
                    color: colors.primary.dark,
                    bgcolor: colors.hover.background,
                  }
                : {},
              '&.Mui-disabled': {
                opacity: 0.4,
              },
            }}
          >
            <FilterIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
      ),
    };
  });

  return (
    <Box
      sx={{
        height: height,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <DataGrid
            rows={rows}
            columns={columnsWithFilter}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize, page: 0 },
              },
            }}
            pageSizeOptions={pageSizeOptions}
            onRowClick={onRowClick}
            disableRowSelectionOnClick
            disableColumnFilter={true}
            disableColumnMenu={true}
            localeText={{
              noRowsLabel: noRowsLabel,
            }}
            slotProps={{
              basePagination: {
                material: {
                  labelRowsPerPage: 'Sayfa başına:',
                  labelDisplayedRows: ({
                    from,
                    to,
                    count,
                  }: {
                    from: number;
                    to: number;
                    count: number;
                  }) =>
                    `${from}–${to} / ${
                      count !== -1 ? count : `${to}'den fazla`
                    }`,
                },
              },
            }}
            rowHeight={rowHeight}
            autoHeight={false}
            sx={{
              border: 'none',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',

              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${colors.border.light}`,
                display: 'flex',
                alignItems: 'center',
                py: 2.5,
                px: 2,
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: colors.background.light,
                borderBottom: 'none',
                minHeight: '64px !important',
                maxHeight: '64px !important',
                lineHeight: '64px !important',
                flexShrink: 0,
                '& .MuiDataGrid-columnHeader': {
                  px: 2,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: colors.text.primary,
                },
              },
              '& .MuiDataGrid-row': {
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                minHeight: `${rowHeight}px !important`,
                maxHeight: `${rowHeight}px !important`,
                '&:hover': onRowClick
                  ? {
                      bgcolor: colors.hover.background,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transform: 'translateY(-1px)',
                    }
                  : {},
                '&:active': onRowClick
                  ? {
                      transform: 'translateY(0)',
                    }
                  : {},
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: 'none',
                bgcolor: colors.background.light,
                minHeight: 64,
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                zIndex: 10,
                '& .MuiDataGrid-selectedRowCount': {
                  display: 'none',
                },
                '& .MuiTablePagination-root': {
                  width: '100%',
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                },
                '& .MuiTablePagination-toolbar': {
                  width: '100%',
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '0 20px',
                  minHeight: 64,
                  overflow: 'hidden',
                  gap: '20px',
                  '& > *': {
                    flexShrink: 0,
                  },
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectLabel': {
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: colors.text.secondary,
                  marginRight: '12px',
                },
                '& .MuiTablePagination-select': {
                  flexShrink: 0,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: colors.text.primary,
                  bgcolor: 'white',
                  paddingRight: 'auto',
                  minWidth: 'auto',
                  width: 'auto',
                  transition: 'all 0.2s ease',

                  '& .MuiSelect-select': {
                    padding: 0,
                    paddingRight: '0 !important',
                    minHeight: 'auto',
                    lineHeight: '1.5',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    display: 'none',
                  },
                  '& .MuiSelect-icon': {
                    display: 'none',
                  },
                },
                '& .MuiTablePagination-displayedRows': {
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  marginLeft: '24px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: colors.text.secondary,
                  letterSpacing: '0.01em',
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  gap: '6px',
                  marginLeft: '24px',
                  flexShrink: 0,
                  '& .MuiIconButton-root': {
                    padding: '8px',
                    margin: 0,
                    borderRadius: '8px',
                    color: colors.text.primary,
                    minWidth: '36px',
                    width: '36px',
                    height: '36px',
                    position: 'relative',
                    zIndex: 1001,
                    bgcolor: 'white',
                    border: `1px solid ${colors.border.light}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: colors.primary.light,
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 3px 8px rgba(67, 160, 71, 0.2)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.4,
                      bgcolor: colors.background.light,
                      borderColor: colors.border.light,
                      cursor: 'not-allowed',
                      '&:hover': {
                        transform: 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      },
                    },
                  },
                },
              },
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'auto !important',
                flex: 1,
                minHeight: 0,
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                height: 'auto !important',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor?.element}
        open={Boolean(filterAnchor)}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            p: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {filterAnchor
            ? columns.find((col) => col.field === filterAnchor.field)
                ?.headerName || filterAnchor.field
            : ''}{' '}
          Filtrele
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Filtre değeri girin..."
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleFilterApply();
            }
          }}
          sx={{ mb: 2, mt: 1 }}
          autoFocus
        />
        <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleFilterClear}
            sx={{
              color: colors.text.secondary,
              borderColor: colors.border.light,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.text.secondary,
                bgcolor: colors.background.light,
              },
            }}
          >
            Temizle
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleFilterApply}
            sx={{
              bgcolor: colors.primary.main,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                bgcolor: colors.hover.primary,
              },
            }}
          >
            Uygula
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CustomDataGrid;
